import { useEffect, useRef, useState } from "react"
import styles from "./SearchableMultiselectDropdown.module.scss"

interface Item {
    id: string
    label: string
}

interface Props {
    items: Item[]
    checkedIds: string[]
    placeholder?: string
    autoFocus?: boolean
    debounceTime?: number
    className?: string
    isLoading?: boolean
    onSearch?: (value: string) => void
    onSelectionChange: (selectedId: string) => void
    handleScroll?: (e: React.UIEvent<HTMLDivElement>) => void
}

const SearchableMultiselectDropdown: React.FC<Props> = ({
    items,
    checkedIds,
    placeholder = "Search",
    autoFocus = false,
    debounceTime = 300,
    className = "",
    isLoading = false,
    onSearch,
    onSelectionChange,
    handleScroll
}) => {
    const [searchValue, setSearchValue] = useState("")
    const [isOpen, setIsOpen] = useState(false)

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus()
        }
    }, [autoFocus])

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    useEffect(() => {
        // Clean up the debounce timer when component unmounts
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setSearchValue(value)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            if (onSearch) {
                onSearch(value)
            }
        }, debounceTime)
    }

    const handleClear = () => {
        setSearchValue("")
        if (onSearch) {
            onSearch("")
        }
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    const internalHandleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (handleScroll)
            handleScroll(e);
    };

    const handleFocus = () => setIsOpen(true)

    const selectedLabels = items
        .filter(item => checkedIds.includes(item.id))
        .map(item => item.label);
    const displayPlaceholder = selectedLabels.length > 0 ? selectedLabels.join(", ") : placeholder;

    return (
        <div ref={dropdownRef} className={styles.dropdownContainer}>
            <div className={`${styles.searchContainer} ${className}`}>
                <input
                    ref={inputRef}
                    type="text"
                    className={styles.searchInput}
                    placeholder={displayPlaceholder}
                    value={searchValue}
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                />
                {searchValue && (
                    <button type="button" className={styles.clearButton} onClick={handleClear} aria-label="Clear search">
                        ×
                    </button>
                )}
            </div>
            {isOpen && (
                <div className={styles.dialog} onScroll={internalHandleScroll}>
                    {isLoading ? (
                        <div className={styles.loadingContainer}>Loading...</div>
                    ) : (
                        <>
                            {items.map(item => (
                                <label key={item.id} className={styles.dialogOptionContainer}>
                                    <input
                                        type="checkbox"
                                        checked={checkedIds.includes(item.id)}
                                        onChange={() => onSelectionChange(item.id)}
                                    />
                                    {item.label}
                                </label>
                            ))}
                            {items.length === 0 && <div className={styles.noResults}>No results</div>}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

export default SearchableMultiselectDropdown;
